import { Edit as EditIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Address,
  BuildingWithoutTrusses,
  CheckoutValidationResponse,
  LoadUnitLabels,
  Order,
  OrderDetailsFormData,
  OrderLoadRatingFormData,
  OrderShippingFormData,
  OrderStatus,
  UserRole,
} from '../../types';
import {
  DateUtils,
  formatPhoneNumber,
  getErrorMessage,
  getOrderStatusColor,
  getOrderStatusLabel,
  logDebug,
  showToast,
  sumPrices,
} from '../../utils';
import { useGetDealerByIdQuery } from '../dealer';
import { selectCurrentUser, selectManageOrders, selectManageShippingLocations } from '../user';
import CheckoutConfirmationDialog from './checkout-confirmation-dialog.tsx';
import ConfirmLoadRatingDialog from './confirm-load-rating-dialog.tsx';
import JobLocationDialog from './job-location-dialog.tsx';
import {
  addressesMatchForJobLocation,
  buildValidatedJobLocationFromAddressValidation,
  hasValidatedJobLocation,
  isOrderJobLocationSatisfied,
  jobLocationToShippingAddress,
} from './job-location-shipping';
import OrderDetailsDialog from './order-details-dialog.tsx';
import OrderLoadRatingDialog from './order-load-rating-dialog.tsx';
import OrderShippingDialog from './order-shipping-dialog.tsx';
import { ORDER_NOTICE_AND_WARNING_TEXT } from './order-notice-text';
import {
  useCheckoutOrderMutation,
  useGetAsceLoadRatingsMutation,
  useGetBuildingsByOrderIdQuery,
  useGetOrderByIdQuery,
  usePlaceOrderOnHoldMutation,
  useReleaseForPickupMutation,
  useReleaseForShipmentMutation,
  useReleaseOrderFromHoldMutation,
  useReleaseToProductionMutation,
  useReturnOrderToDraftMutation,
  useShipOrderMutation,
  useSignOrderMutation,
  useUpdateOrderMutation,
  useValidateAddressMutation,
  useValidateCheckoutMutation,
} from './orders-api-slice';

interface OrderDetailsPageProps {
  order?: Order;
}

const Section = ({
  title,
  children,
  showEditIcon = false,
  onEdit,
}: {
  title: string;
  children: React.ReactNode;
  showEditIcon?: boolean;
  onEdit?: () => void;
}) => (
  <Box
    sx={{
      position: 'relative',
      height: '100%',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      pt: 3,
      pb: 2,
      px: 2,
    }}
  >
    <Typography
      variant="subtitle1"
      component="h2"
      fontWeight="500"
      sx={{
        position: 'absolute',
        top: 0,
        transform: 'translateY(-50%)',
        backgroundColor: 'background.paper',
        px: 1,
        ml: 1,
      }}
    >
      {title}
    </Typography>

    {/* Edit icon in lower right corner */}
    {showEditIcon && onEdit && (
      <IconButton
        onClick={onEdit}
        size="small"
        sx={{
          position: 'absolute',
          bottom: 8,
          right: 8,
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <EditIcon sx={{ fontSize: 16 }} />
      </IconButton>
    )}

    {children}
  </Box>
);

// Custom Section component for Building Information with different layout
const BuildingInfoSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box
    sx={{
      position: 'relative',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      pt: 3,
      pb: 2,
      px: 2,
    }}
  >
    <Typography
      variant="subtitle1"
      component="h2"
      fontWeight="500"
      sx={{
        position: 'absolute',
        top: 0,
        transform: 'translateY(-50%)',
        backgroundColor: 'background.paper',
        px: 1,
        ml: 1,
      }}
    >
      {title}
    </Typography>

    {children}
  </Box>
);

// Field component for consistent label-value alignment
const Field = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string | React.ReactNode;
  color?: string;
}) => (
  <Box sx={{ display: 'flex', mb: 1.5 }}>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600,
        minWidth: '120px',
        flexShrink: 0,
        color: 'text.secondary',
        fontSize: '0.875rem',
      }}
    >
      {label && label.trim() !== '' ? `${label}:` : ''}
    </Typography>
    <Typography
      variant="body1"
      sx={{
        ml: 2,
        color: color || 'text.primary',
        fontWeight: 500,
        fontSize: '0.875rem',
      }}
    >
      {value}
    </Typography>
  </Box>
);

// Horizontal field component for the building info top row with label on left
const HorizontalField = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string | React.ReactNode;
  color?: string;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      minWidth: 'fit-content',
    }}
  >
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600,
        mr: 2,
        color: 'text.secondary',
        fontSize: '0.875rem',
      }}
    >
      {label}:
    </Typography>
    <Typography
      variant="body1"
      sx={{
        color: color || 'text.primary',
        fontWeight: 500,
        fontSize: '0.875rem',
      }}
    >
      {value}
    </Typography>
  </Box>
);

const OrderDetailsHeader: React.FC<OrderDetailsPageProps> = ({ order: propOrder }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canManageOrders = useSelector(selectManageOrders);
  const canManageShippingLocations = useSelector(selectManageShippingLocations);
  const currentUser = useSelector(selectCurrentUser);

  // Use the API hook to fetch the order if not provided as prop
  const {
    data: fetchedOrder,
    isLoading,
    error,
  } = useGetOrderByIdQuery(id!, {
    skip: !id || !!propOrder, // Skip if no id or order is already provided as prop
  });
  // Hook for updating the order
  const [updateOrder, { isLoading: isUpdating }] = useUpdateOrderMutation();
  const [placeOrderOnHold, { isLoading: isPlacingOnHold }] = usePlaceOrderOnHoldMutation();
  const [shipOrder, { isLoading: isShipping }] = useShipOrderMutation();
  const [releaseForPickup, { isLoading: isReleasingForPickup }] = useReleaseForPickupMutation();
  const [releaseForShipment, { isLoading: isReleasing }] = useReleaseForShipmentMutation();
  const [signOrder, { isLoading: isSigning }] = useSignOrderMutation();
  const [validateCheckout] = useValidateCheckoutMutation();
  const [checkoutOrder, { isLoading: isCheckingOut }] = useCheckoutOrderMutation();
  const [returnOrderToDraft, { isLoading: isReturningToDraft }] = useReturnOrderToDraftMutation();
  const [releaseToProduction, { isLoading: isReleasingToProduction }] =
    useReleaseToProductionMutation();
  const [releaseOrderFromHold, { isLoading: isReleasingFromHold }] =
    useReleaseOrderFromHoldMutation();

  // Use the prop order if available, otherwise use the fetched order
  const order = propOrder || fetchedOrder;
  // Always call the dealer hook, but skip if no order or no dealerId
  const { data: dealer } = useGetDealerByIdQuery(order?.dealerId?.toString() || '', {
    skip: !order?.dealerId,
  });

  // Get buildings to calculate cart total and engineering fees
  const { data: buildings = [] } = useGetBuildingsByOrderIdQuery(id!, {
    skip: !id,
  });

  // Engineering fees are now included in the order response
  const engineeringFees = order?.engineeringFees;

  const cartTotal = sumPrices(buildings.map(b => b.totalPrice || 0));

  // Calculate engineering fees totals from all buildings
  const engineeringFeesSummary = useMemo(() => {
    if (!engineeringFees || !buildings.length) {
      return {
        openPoleBarn: { count: 0, total: 0 },
        partialClosed: { count: 0, total: 0 },
        fullyEnclosed: { count: 0, total: 0 },
        permanentDwelling: { count: 0, total: 0 },
      };
    }

    const summary = {
      openPoleBarn: { count: 0, total: 0 },
      partialClosed: { count: 0, total: 0 },
      fullyEnclosed: { count: 0, total: 0 },
      permanentDwelling: { count: 0, total: 0 },
    };

    buildings.forEach((building: BuildingWithoutTrusses) => {
      if (building.includePermanentDwellingWithPoleBarn) {
        summary.openPoleBarn.count++;
        summary.openPoleBarn.total += engineeringFees.openPoleBarnFee;
      }
      if (building.includePermanentDwellingForPartiallyClosedPoleBarn) {
        summary.partialClosed.count++;
        summary.partialClosed.total += engineeringFees.partialClosedPoleBarnFee;
      }
      if (building.includePermanentDwellingForFullyEnclosedPoleBarn) {
        summary.fullyEnclosed.count++;
        summary.fullyEnclosed.total += engineeringFees.fullyEnclosedPoleBarnFee;
      }
      if (building.includePermanentDwellingWithNoMEP) {
        summary.permanentDwelling.count++;
        summary.permanentDwelling.total += engineeringFees.permanentDwellingFee;
      }
    });

    return summary;
  }, [buildings, engineeringFees]);

  // Dialog state management
  const [orderDetailsDialogOpen, setOrderDetailsDialogOpen] = useState(false);
  const [orderShippingDialogOpen, setOrderShippingDialogOpen] = useState(false);
  const [orderBuildingInfoDialogOpen, setOrderBuildingInfoDialogOpen] = useState(false);
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [checkoutValidation, setCheckoutValidation] = useState<CheckoutValidationResponse | null>(
    null
  );
  const [jobLocationDialogOpen, setJobLocationDialogOpen] = useState(false);
  const [confirmLoadRatingOpen, setConfirmLoadRatingOpen] = useState(false);
  const [pendingAsceData, setPendingAsceData] = useState<{
    windLoad: number;
    snowLoad: number;
    previousWindLoad: number;
    previousSnowLoad: number;
    jobLocation: Order['jobLocation'];
    additionalOrderFields?: Pick<Order, 'pickup' | 'shippingAddress'>;
  } | null>(null);

  const [getAsceLoadRatings, { isLoading: isLoadingAsce }] = useGetAsceLoadRatingsMutation();
  const [validateAddress] = useValidateAddressMutation();

  // Signature state
  const [signatureName, setSignatureName] = useState(order?.signedBy || '');
  const [signatureDate, setSignatureDate] = useState(
    order?.signedDate ? DateUtils.toDisplayDateTime(order.signedDate) : ''
  );

  // Common container styles - removed fixed height to allow scrolling
  const containerStyles = {
    width: '100%',
    minHeight: '100vh',
    backgroundColor: 'background.paper',
    borderRadius: 1,
    p: 3,
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          ...containerStyles,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading order details...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={containerStyles}>
        <Typography variant="h6" color="error">
          Error loading order details
        </Typography>
      </Box>
    );
  }

  if (!order) {
    return (
      <Box sx={containerStyles}>
        <Typography variant="h6">Order not found</Typography>
      </Box>
    );
  }

  const isOrderDraft = order.status === OrderStatus.DRAFT;
  const isOrderCheckedOut = order.status === OrderStatus.CHECKED_OUT;
  const isOrderInQueue = order.status === OrderStatus.IN_QUEUE;
  const isOrderInProduction =
    order.status === OrderStatus.IN_QUEUE ||
    order.status === OrderStatus.SAW ||
    order.status === OrderStatus.WELD ||
    order.status === OrderStatus.PAINT;
  const isOrderShipped = order.status === OrderStatus.SHIPPED;
  const isOrderComplete = order.status === OrderStatus.COMPLETE;
  const isOrderOnHold = order.status === OrderStatus.ON_HOLD;
  const isOrderReadyForPickup = order.status === OrderStatus.READY_FOR_PICKUP;
  const isOrderReadyForShipment = order.status === OrderStatus.READY_FOR_SHIPMENT;
  const isOrderReadyToShip = isOrderReadyForPickup || isOrderReadyForShipment;
  const isPostProduction = isOrderComplete || isOrderOnHold || isOrderReadyToShip || isOrderShipped;
  const isLockedFromEditing = isOrderCheckedOut || isPostProduction;
  const canEditOrder = canManageOrders && !isLockedFromEditing;
  const canEditShipping = canManageShippingLocations && !isPostProduction;

  const handleEditOrderDetails = () => {
    if (isPostProduction) {
      showToast('This order is past production and cannot be modified', 'warning');
      return;
    }
    setOrderDetailsDialogOpen(true);
  };

  const handleOrderDetailsClose = () => {
    setOrderDetailsDialogOpen(false);
  };

  const handleOrderDetailsSubmit = async (data: OrderDetailsFormData) => {
    if (!order?.id) return;

    try {
      logDebug('Saving order details', data);

      const updatedOrder = {
        ...order,
        ...data,
        id: order.id,
      };

      await updateOrder(updatedOrder).unwrap();
      setOrderDetailsDialogOpen(false);
    } catch (error) {
      logDebug('Error updating order:', error);
      throw error;
    }
  };

  // Shipping handlers
  const handleEditShippingInfo = () => {
    if (isPostProduction) {
      showToast('This order is past production and cannot be modified', 'warning');
      return;
    }
    setOrderShippingDialogOpen(true);
  };

  const handleShippingClose = () => {
    setOrderShippingDialogOpen(false);
  };

  const handleShippingSubmit = async (data: OrderShippingFormData) => {
    if (!order?.id) return;

    if (data.pickup && !isOrderJobLocationSatisfied(order.jobLocation)) {
      showToast(
        'Pickup orders require a job location. Edit Job Location to add and validate the jobsite address.',
        'error'
      );
      throw new Error('Pickup order missing job location');
    }

    try {
      logDebug('Saving shipping information', {
        pickup: data.pickup,
        useBillingAddress: data.useBillingAddress,
        hasShippingAddress: !!data.shippingAddress,
      });

      // Determine shipping address based on selection
      let shippingAddress: Address | undefined;
      if (data.pickup || data.useBillingAddress) {
        // Explicitly set to undefined when using pickup or billing address
        shippingAddress = undefined;
      } else {
        shippingAddress = data.shippingAddress;
      }

      const shippingUpdates: Pick<Order, 'pickup' | 'shippingAddress'> = {
        pickup: !!data.pickup,
        shippingAddress,
      };

      // When shipping uses billing address, sync the validated jobsite from billing (UDOMI-999).
      if (!data.pickup && data.useBillingAddress && order.billingAddress?.address1) {
        const needsJobLocationSync =
          !hasValidatedJobLocation(order.jobLocation) ||
          !addressesMatchForJobLocation(order.jobLocation!, order.billingAddress);

        if (needsJobLocationSync) {
          const billing = order.billingAddress;
          const validationResult = await validateAddress({
            address1: billing.address1 ?? '',
            address2: billing.address2 ?? '',
            city: billing.city ?? '',
            state: billing.state ?? '',
            zip: billing.zip ?? '',
          }).unwrap();

          if (!validationResult.isValid) {
            showToast(
              'Unable to validate the billing address for job location. Please edit Job Location to enter the jobsite address manually.',
              'error'
            );
            throw new Error('Billing address validation failed');
          }

          const jobLocation = buildValidatedJobLocationFromAddressValidation(
            billing,
            validationResult
          );
          const hasPermanentDwelling = buildings.some(b => b.isPermanentDwelling);
          const riskLevel = hasPermanentDwelling ? 2 : 1;
          const asceResult = await getAsceLoadRatings({
            lat: validationResult.latitude,
            lon: validationResult.longitude,
            riskLevel,
          }).unwrap();

          setOrderShippingDialogOpen(false);

          if (asceResult.snowParseWarning) {
            showToast(
              'Snow load could not be determined from ASCE data for this location. Defaulting to 0 psf — please verify and override if needed.',
              'warning'
            );
          }

          if (
            asceResult.windLoadMph !== order.windLoad ||
            asceResult.snowLoadPsf !== order.snowLoad
          ) {
            setPendingAsceData({
              windLoad: asceResult.windLoadMph,
              snowLoad: asceResult.snowLoadPsf,
              previousWindLoad: order.windLoad,
              previousSnowLoad: order.snowLoad,
              jobLocation,
              additionalOrderFields: shippingUpdates,
            });
            setConfirmLoadRatingOpen(true);
            return;
          }

          await updateOrder({
            ...order,
            ...shippingUpdates,
            jobLocation,
            id: order.id,
          }).unwrap();
          return;
        }
      }

      // Create the updated order object with explicit field assignments
      const updatedOrder: Order & { id: number } = {
        ...order,
        ...shippingUpdates,
        id: order.id,
      };

      logDebug('Updating order with:', {
        pickup: updatedOrder.pickup,
        hasShippingAddress: !!updatedOrder.shippingAddress,
      });

      await updateOrder(updatedOrder).unwrap();
      setOrderShippingDialogOpen(false);
    } catch (error) {
      logDebug('Error updating shipping information:', error);
      throw error;
    }
  };

  // Building info handlers
  const handleEditBuildingInfo = () => {
    setOrderBuildingInfoDialogOpen(true);
  };

  const handleBuildingInfoClose = () => {
    setOrderBuildingInfoDialogOpen(false);
  };

  const handleBuildingInfoSubmit = async (data: OrderLoadRatingFormData) => {
    if (!order?.id) return;

    try {
      logDebug('Saving building information', data);

      // Create the updated order object with the form data
      const updatedOrder = {
        ...order,
        ...data,
        id: order.id,
      };

      await updateOrder(updatedOrder).unwrap();
      setOrderBuildingInfoDialogOpen(false);
    } catch (error) {
      logDebug('Error updating building information:', error);
      throw error;
    }
  };

  // Job Location handlers
  const handleEditJobLocation = () => {
    setJobLocationDialogOpen(true);
  };

  const handleJobLocationClose = () => {
    setJobLocationDialogOpen(false);
  };

  const handleJobLocationSubmit = async (
    jobLocation: Order['jobLocation'],
    latitude: number,
    longitude: number
  ) => {
    if (!order?.id || !jobLocation) return;

    try {
      const hasPermanentDwelling = buildings.some(b => b.isPermanentDwelling);
      const riskLevel = hasPermanentDwelling ? 2 : 1;

      const asceResult = await getAsceLoadRatings({
        lat: latitude,
        lon: longitude,
        riskLevel,
      }).unwrap();

      setJobLocationDialogOpen(false);

      if (asceResult.snowParseWarning) {
        showToast(
          'Snow load could not be determined from ASCE data for this location. Defaulting to 0 psf — please verify and override if needed.',
          'warning'
        );
      }

      if (asceResult.windLoadMph !== order.windLoad || asceResult.snowLoadPsf !== order.snowLoad) {
        setPendingAsceData({
          windLoad: asceResult.windLoadMph,
          snowLoad: asceResult.snowLoadPsf,
          previousWindLoad: order.windLoad,
          previousSnowLoad: order.snowLoad,
          jobLocation,
        });
        setConfirmLoadRatingOpen(true);
      } else {
        const updatedOrder: Order & { id: number } = {
          ...order,
          jobLocation,
          id: order.id,
        };
        if (!order.pickup && hasValidatedJobLocation(jobLocation)) {
          updatedOrder.shippingAddress = jobLocationToShippingAddress(
            jobLocation,
            order.shippingAddress ?? undefined
          );
        }
        await updateOrder(updatedOrder).unwrap();
      }
    } catch (error) {
      logDebug('Error fetching ASCE data for new location:', error);
      const message = getErrorMessage(error);
      showToast(
        message && message !== 'An unexpected error occurred.'
          ? message
          : 'Unable to retrieve load ratings from ASCE. Please try again later.',
        'error'
      );
    }
  };

  const handleConfirmLoadRatingUpdate = async () => {
    if (!order?.id || !pendingAsceData) return;

    try {
      const updatedOrder: Order & { id: number } = {
        ...order,
        windLoad: pendingAsceData.windLoad,
        snowLoad: pendingAsceData.snowLoad,
        jobLocation: pendingAsceData.jobLocation,
        confirmedLoadRating: true,
        ...(pendingAsceData.additionalOrderFields ?? {}),
        id: order.id,
      };
      const effectivePickup =
        pendingAsceData.additionalOrderFields?.pickup ?? updatedOrder.pickup ?? order.pickup;
      if (!effectivePickup && hasValidatedJobLocation(pendingAsceData.jobLocation)) {
        updatedOrder.shippingAddress = jobLocationToShippingAddress(
          pendingAsceData.jobLocation,
          order.shippingAddress ?? undefined
        );
      }

      await updateOrder(updatedOrder).unwrap();
      setConfirmLoadRatingOpen(false);
      setPendingAsceData(null);
      showToast(
        `Load ratings updated — Wind: ${pendingAsceData.windLoad} mph, Snow: ${pendingAsceData.snowLoad} psf`,
        'success'
      );
    } catch (error) {
      logDebug('Error saving updated load ratings:', error);
      showToast('Failed to save updated load ratings', 'error');
    }
  };

  const isAdminOrSupervisor =
    currentUser &&
    (currentUser.role === UserRole.ADMINISTRATOR || currentUser.role === UserRole.SUPERVISOR);
  const isDealer = currentUser?.role === UserRole.DEALER;

  const handlePlaceOnHold = async () => {
    if (!order?.id) return;

    try {
      await placeOrderOnHold(order.id).unwrap();
      showToast('Order has been placed on hold', 'success');
    } catch (error) {
      logDebug('Error placing order on hold:', error);
      showToast('Failed to place order on hold', 'error');
    }
  };

  const handleReleaseForPickup = async () => {
    if (!order?.id) return;

    try {
      await releaseForPickup(order.id).unwrap();
      showToast('Order has been released for pickup', 'success');
    } catch (error) {
      logDebug('Error releasing order for pickup:', error);
      showToast('Failed to release order for pickup', 'error');
    }
  };

  const handleReleaseForShipment = async () => {
    if (!order?.id) return;

    try {
      await releaseForShipment(order.id).unwrap();
      showToast('Order has been released for shipment', 'success');
    } catch (error) {
      logDebug('Error releasing order for shipment:', error);
      showToast('Failed to release order for shipment', 'error');
    }
  };

  const handleShipOrder = async () => {
    if (!order?.id) return;

    try {
      await shipOrder(order.id).unwrap();
      showToast('Order has been marked as shipped', 'success');
    } catch (error) {
      logDebug('Error shipping order:', error);
      showToast('Failed to ship order', 'error');
    }
  };

  const handleSignOrder = async () => {
    if (!order?.id || !signatureName.trim()) {
      showToast('Please enter your name to sign the order', 'warning');
      return;
    }

    try {
      await signOrder({ orderId: order.id, request: { signedBy: signatureName.trim() } }).unwrap();
      setSignatureDate(DateUtils.toDisplayDateTime(new Date().toISOString()));
      showToast('Order has been signed successfully', 'success');
    } catch (error) {
      logDebug('Error signing order:', error);
      showToast('Failed to sign the order', 'error');
    }
  };

  const handleCheckout = async () => {
    if (!order?.id) return;

    try {
      const validation = await validateCheckout(order.id).unwrap();

      if (!validation.isValid) {
        validation.errors.forEach(err => showToast(err, 'error'));
        return;
      }

      setCheckoutValidation(validation);
      setCheckoutDialogOpen(true);
    } catch (error) {
      logDebug('Error validating checkout:', error);
      showToast('Failed to validate order for checkout', 'error');
    }
  };

  const handleConfirmCheckout = async (proposedDueDate?: string) => {
    if (!order?.id) return;

    try {
      const result = await checkoutOrder({
        orderId: order.id,
        request: proposedDueDate ? { proposedDueDate } : undefined,
      }).unwrap();
      setCheckoutDialogOpen(false);
      const dueDateDisplay = result.dueDate ? DateUtils.toDisplayDate(result.dueDate) : '';
      showToast(`Order has been checked out successfully. Due Date: ${dueDateDisplay}`, 'success');
    } catch (error) {
      logDebug('Error checking out order:', error);
      showToast('Failed to check out order', 'error');
    }
  };

  const handleReturnToDraft = async () => {
    if (!order?.id) return;

    try {
      await returnOrderToDraft(order.id).unwrap();
      showToast('Order has been returned to Draft status', 'success');
    } catch (error) {
      logDebug('Error returning order to draft:', error);
      showToast('Failed to return order to Draft', 'error');
    }
  };

  const handleReleaseToProduction = async () => {
    if (!order?.id) return;

    try {
      await releaseToProduction(order.id).unwrap();
      showToast('Order has been released to production', 'success');
      navigate('/dashboard');
    } catch (error) {
      logDebug('Error releasing order to production:', error);
      showToast('Failed to release order to production', 'error');
    }
  };

  const handleReleaseFromHold = async () => {
    if (!order?.id) return;

    try {
      await releaseOrderFromHold(order.id).unwrap();
      showToast('Order has been released from hold', 'success');
    } catch (error) {
      logDebug('Error releasing order from hold:', error);
      showToast('Failed to release order from hold', 'error');
    }
  };

  const renderShippingContent = (order: Order) => {
    if (order.pickup) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            minHeight: '120px', // Ensure adequate height for centering
          }}
        >
          <Typography variant="body1">Customer pickup</Typography>
        </Box>
      );
    }

    const shippingDisplay = order.shippingAddress?.address1
      ? order.shippingAddress
      : hasValidatedJobLocation(order.jobLocation)
        ? jobLocationToShippingAddress(order.jobLocation)
        : null;

    if (shippingDisplay?.address1) {
      return (
        <>
          <Field label="Address" value={shippingDisplay.address1} />
          {shippingDisplay.address2 && <Field label="" value={shippingDisplay.address2} />}
          <Field
            label="City/State"
            value={`${shippingDisplay.city}, ${shippingDisplay.state} ${shippingDisplay.zip}`}
          />
          {shippingDisplay.phone && (
            <Field label="Phone" value={formatPhoneNumber(shippingDisplay.phone)} />
          )}
        </>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          minHeight: '120px', // Ensure adequate height for centering
        }}
      >
        <Typography variant="body1">Same as billing address</Typography>
      </Box>
    );
  };

  return (
    <Box sx={containerStyles}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.25rem' }}>
          Order Details: {order.orderNumber}
        </Typography>
        <Chip
          label={getOrderStatusLabel(order.status)}
          size="small"
          sx={{
            backgroundColor: getOrderStatusColor(order.status),
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.75rem',
          }}
        />
      </Box>
      {isOrderCheckedOut && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          This order has been checked out and released into production. No modifications can be
          made.
          {isAdminOrSupervisor && ' You may return it to Draft status to allow changes.'}
          {order.checkedOutDate &&
            ` Checked out on ${DateUtils.toDisplayDateTime(order.checkedOutDate)}.`}
        </Alert>
      )}
      {isPostProduction && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {isOrderShipped
            ? 'This order has been finalized and shipped. No modifications are allowed.'
            : isOrderOnHold
              ? 'This order is on hold. Only Admins and Supervisors may change its status.'
              : isOrderComplete
                ? 'This order is complete. It may be held or released for shipment.'
                : 'This order is ready for shipment. It may be held or marked as shipped.'}
        </Alert>
      )}
      <Divider sx={{ mb: 6 }} />

      <Grid container spacing={3}>
        {/* First row with three sections */}
        <Grid item xs={12} md={4}>
          <Section
            title="Order Details"
            showEditIcon={canEditOrder}
            onEdit={handleEditOrderDetails}
          >
            <Box>
              <Field label="Customer Name" value={dealer?.name || 'N/A'} />
              <Field
                label="Status"
                value={
                  <>
                    <Typography
                      component="span"
                      sx={{
                        color: getOrderStatusColor(order.status),
                        fontWeight: 500,
                        fontSize: '0.875rem',
                      }}
                    >
                      {getOrderStatusLabel(order.status)}
                    </Typography>
                    {(order.status === OrderStatus.IN_QUEUE && order.inQueueDate
                      ? order.inQueueDate
                      : order.statusChangedAt) && (
                      <Typography
                        component="span"
                        sx={{ fontSize: '0.75rem', color: 'text.secondary', ml: 1 }}
                      >
                        (
                        {DateUtils.toDisplayDateTime(
                          order.status === OrderStatus.IN_QUEUE && order.inQueueDate
                            ? order.inQueueDate
                            : order.statusChangedAt!
                        )}
                        )
                      </Typography>
                    )}
                  </>
                }
              />
              <Field label="Order Number" value={order.orderNumber} />
              <Field label="PO" value={order.po} />
              <Field label="Job Name" value={order.jobName} />
              <Field label="Order Date" value={DateUtils.toDisplayDateTime(order.orderDate)} />
              <Field
                label="Due Date"
                value={
                  order.dueDate ? (
                    <>
                      {DateUtils.toDisplayDate(order.dueDate)}
                      {order.dueDateChangedAt && (
                        <Typography
                          component="span"
                          sx={{ fontSize: '0.75rem', color: 'text.secondary', ml: 1 }}
                        >
                          (updated {DateUtils.toDisplayDateTime(order.dueDateChangedAt)})
                        </Typography>
                      )}
                    </>
                  ) : (
                    'Pending'
                  )
                }
              />
              {order.orderSubmittedDate && (
                <Field
                  label="Order Submitted"
                  value={DateUtils.toDisplayDateTime(order.orderSubmittedDate)}
                />
              )}
              {order.completionDate && (
                <Field
                  label="Completed"
                  value={DateUtils.toDisplayDateTime(order.completionDate)}
                />
              )}
              {order.checkedOutDate && order.status >= 2 && (
                <Field
                  label="Checked Out"
                  value={DateUtils.toDisplayDateTime(order.checkedOutDate)}
                />
              )}
              {order.inQueueDate && order.status >= 3 && (
                <Field
                  label="Released to Production"
                  value={DateUtils.toDisplayDateTime(order.inQueueDate)}
                />
              )}
              {order.onHoldDate && (
                <Field
                  label="On Hold Since"
                  value={DateUtils.toDisplayDateTime(order.onHoldDate)}
                />
              )}
              {order.readyForShipmentDate && (
                <Field
                  label="Released for Shipment"
                  value={DateUtils.toDisplayDateTime(order.readyForShipmentDate)}
                />
              )}
              {order.shippedDate && (
                <Field label="Shipped" value={DateUtils.toDisplayDateTime(order.shippedDate)} />
              )}
            </Box>
          </Section>
        </Grid>

        <Grid item xs={12} md={4}>
          <Section title="Billing Information">
            <Box>
              <Field label="Address 1" value={order.billingAddress.address1} />
              <Field label="Address 2" value={order.billingAddress.address2 || ''} />
              <Field label="City" value={order.billingAddress.city} />
              <Field label="State" value={order.billingAddress.state} />
              <Field label="Zip" value={order.billingAddress.zip} />
              <Field
                label="Phone"
                value={
                  order.billingAddress.phone ? formatPhoneNumber(order.billingAddress.phone) : ''
                }
              />
              <Field
                label="Fax"
                value={order.billingAddress.fax ? formatPhoneNumber(order.billingAddress.fax) : ''}
              />
              <Field label="Email" value={order.billingAddress.email || ''} />
            </Box>
          </Section>
        </Grid>

        <Grid item xs={12} md={4}>
          <Section
            title="Shipping Information"
            showEditIcon={canEditShipping}
            onEdit={handleEditShippingInfo}
          >
            <Box>{renderShippingContent(order)}</Box>
          </Section>
        </Grid>

        {/* Second row: Job Location + Building Information */}
        <Grid item xs={12} md={6} sx={{ mb: 1 }}>
          <Section title="Job Location" showEditIcon={canEditOrder} onEdit={handleEditJobLocation}>
            <Box>
              {order.jobLocation ? (
                <>
                  <Field label="Address" value={order.jobLocation.address1 || 'N/A'} />
                  <Field
                    label="City/State/Zip"
                    value={
                      order.jobLocation.city
                        ? `${order.jobLocation.city}, ${order.jobLocation.state} ${order.jobLocation.zip}`
                        : 'N/A'
                    }
                  />
                  <Field
                    label="Coordinates"
                    value={
                      order.jobLocation.latitude && order.jobLocation.longitude
                        ? `${order.jobLocation.latitude.toFixed(4)}, ${order.jobLocation.longitude.toFixed(4)}`
                        : 'N/A'
                    }
                  />
                  {order.jobLocation.validationStatus === 'validated' && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'success.main',
                        fontWeight: 500,
                        mt: 0.5,
                        '&::before': { content: '"\\2713 "' },
                      }}
                    >
                      Address &amp; Coordinates validated
                    </Typography>
                  )}
                  {order.jobLocation.validationStatus === 'override' && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'warning.main',
                        fontWeight: 500,
                        mt: 0.5,
                        '&::before': { content: '"\\26A0 "' },
                      }}
                    >
                      Entered without validation (address validation override)
                    </Typography>
                  )}
                  {order.confirmedLoadRating && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'success.main',
                        fontWeight: 500,
                        mt: 0.5,
                        '&::before': { content: '"\\2713 "' },
                      }}
                    >
                      ASCE Load Ratings confirmed — Wind: {order.windLoad} mph, Snow:{' '}
                      {order.snowLoad} psf
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No job location set. Click edit to add one.
                </Typography>
              )}
            </Box>
          </Section>
        </Grid>

        <Grid item xs={12} md={6} sx={{ mb: 1 }}>
          <BuildingInfoSection title="Building Information">
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: 3,
                    alignItems: 'center',
                    flex: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <HorizontalField
                    label="Live Load"
                    value={`${order.liveLoad}\u00A0\u00A0${LoadUnitLabels.WEIGHT}`}
                  />
                  <HorizontalField
                    label="Dead Load"
                    value={`${order.deadLoad}\u00A0\u00A0${LoadUnitLabels.WEIGHT}`}
                  />
                  <HorizontalField
                    label="Wind Load"
                    value={`${order.windLoad}\u00A0\u00A0${LoadUnitLabels.WIND}`}
                  />
                  <HorizontalField
                    label="Snow Load"
                    value={`${order.snowLoad}\u00A0\u00A0${LoadUnitLabels.SNOW}`}
                  />
                  <HorizontalField
                    label="Confirmed"
                    value={order.confirmedLoadRating ? 'Yes' : 'No'}
                  />
                </Box>

                {canEditOrder && (
                  <IconButton
                    onClick={handleEditBuildingInfo}
                    size="small"
                    sx={{
                      backgroundColor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                      ml: 2,
                    }}
                  >
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>
            </Box>
          </BuildingInfoSection>
        </Grid>

        {/* Status History Timeline */}
        {order.statusHistory && order.statusHistory.length > 0 && (
          <Grid item xs={12}>
            <Section title="Order Status History">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {order.statusHistory.map(entry => (
                  <Box
                    key={entry.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      py: 0.75,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.8rem',
                        color: 'text.secondary',
                        minWidth: 170,
                        flexShrink: 0,
                      }}
                    >
                      {DateUtils.toDisplayDateTime(entry.changedAt)}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {getOrderStatusLabel(entry.fromStatus)} →{' '}
                      {getOrderStatusLabel(entry.toStatus)}
                    </Typography>
                    {entry.changedByUserName && (
                      <Typography
                        variant="body2"
                        sx={{ fontSize: '0.8rem', color: 'text.secondary' }}
                      >
                        by {entry.changedByUserName}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Section>
          </Grid>
        )}

        {/* Third row: Engineering Plan Fees (left) + Your Cart Total (right) */}
        <Grid item xs={12} md={8}>
          {engineeringFees ? (
            <Section title="Engineered Plan Fees Included">
              <Box>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  These are all the Engineering fees that have been included in this Order.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {/* Open Pole Barn */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={engineeringFeesSummary.openPoleBarn.count > 0}
                          disabled
                        />
                      }
                      label={`Open Pole Barn - $${engineeringFees.openPoleBarnFee.toFixed(2)}`}
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {engineeringFeesSummary.openPoleBarn.count > 0
                        ? `* ${
                            engineeringFeesSummary.openPoleBarn.count
                          } = $${engineeringFeesSummary.openPoleBarn.total.toFixed(2)}`
                        : '$0.00'}
                    </Typography>
                  </Box>

                  {/* Partial Closed Pole Barn */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={engineeringFeesSummary.partialClosed.count > 0}
                          disabled
                        />
                      }
                      label={`Partial Closed Pole Barn - $${engineeringFees.partialClosedPoleBarnFee.toFixed(
                        2
                      )}`}
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {engineeringFeesSummary.partialClosed.count > 0
                        ? `* ${
                            engineeringFeesSummary.partialClosed.count
                          } = $${engineeringFeesSummary.partialClosed.total.toFixed(2)}`
                        : '$0.00'}
                    </Typography>
                  </Box>

                  {/* Fully Enclosed Pole Barn */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={engineeringFeesSummary.fullyEnclosed.count > 0}
                          disabled
                        />
                      }
                      label={`Fully Enclosed Pole Barn - $${engineeringFees.fullyEnclosedPoleBarnFee.toFixed(
                        2
                      )}`}
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {engineeringFeesSummary.fullyEnclosed.count > 0
                        ? `* ${
                            engineeringFeesSummary.fullyEnclosed.count
                          } = $${engineeringFeesSummary.fullyEnclosed.total.toFixed(2)}`
                        : '$0.00'}
                    </Typography>
                  </Box>

                  {/* Permanent Dwelling (no MEP) */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={engineeringFeesSummary.permanentDwelling.count > 0}
                          disabled
                        />
                      }
                      label={`Permanent Dwelling (no MEP) - $${engineeringFees.permanentDwellingFee.toFixed(
                        2
                      )}`}
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {engineeringFeesSummary.permanentDwelling.count > 0
                        ? `* ${
                            engineeringFeesSummary.permanentDwelling.count
                          } = $${engineeringFeesSummary.permanentDwelling.total.toFixed(2)}`
                        : '$0.00'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Section>
          ) : (
            <Section title="Engineered Plan Fees">
              <Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  No Engineering Fees are associated with the Manufacturer for this Order.
                </Typography>
              </Box>
            </Section>
          )}
        </Grid>

        {/* Your Cart Total Section - Right */}
        {order?.id && (
          <Grid item xs={12} md={4}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                height: '100%',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body2"
                  sx={{ mb: 0.5, color: 'text.secondary', fontWeight: 500 }}
                >
                  Your Cart Total
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  ${cartTotal.toFixed(2)}
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Draft: Checkout */}
                {isOrderDraft && canManageOrders && (
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    onClick={handleCheckout}
                    disabled={isCheckingOut}
                    sx={{ py: 1.5 }}
                  >
                    {isCheckingOut ? 'Processing...' : 'Checkout'}
                  </Button>
                )}

                {/* Checked Out: Release to Production + Return to Draft */}
                {isOrderCheckedOut && isAdminOrSupervisor && (
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    onClick={handleReleaseToProduction}
                    disabled={isReleasingToProduction}
                    sx={{ py: 1.5 }}
                  >
                    {isReleasingToProduction ? 'Releasing...' : 'Release to Production'}
                  </Button>
                )}
                {(isOrderCheckedOut || isOrderInQueue) && isAdminOrSupervisor && (
                  <Button
                    variant="outlined"
                    color="warning"
                    fullWidth
                    onClick={handleReturnToDraft}
                    disabled={isReturningToDraft}
                    sx={{ py: 1.5 }}
                  >
                    {isReturningToDraft ? 'Returning...' : 'Return to Draft'}
                  </Button>
                )}

                {/* In Queue / Saw / Weld / Paint: Hold the Order */}
                {isOrderInProduction && isAdminOrSupervisor && (
                  <Button
                    variant="contained"
                    color="error"
                    fullWidth
                    onClick={handlePlaceOnHold}
                    disabled={isPlacingOnHold}
                    sx={{ py: 1.5 }}
                  >
                    {isPlacingOnHold ? 'Placing on Hold...' : 'Hold the Order'}
                  </Button>
                )}

                {/* Complete: Hold the Order + Release for Shipment */}
                {isAdminOrSupervisor && isOrderComplete && (
                  <>
                    <Button
                      variant="contained"
                      color="error"
                      fullWidth
                      onClick={handlePlaceOnHold}
                      disabled={isPlacingOnHold}
                      sx={{ py: 1.5 }}
                    >
                      {isPlacingOnHold ? 'Placing on Hold...' : 'Hold the Order'}
                    </Button>
                    {order.pickup ? (
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={handleReleaseForPickup}
                        disabled={isReleasingForPickup}
                        sx={{ py: 1.5 }}
                      >
                        {isReleasingForPickup ? 'Releasing...' : 'Release for Pickup'}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={handleReleaseForShipment}
                        disabled={isReleasing}
                        sx={{ py: 1.5 }}
                      >
                        {isReleasing ? 'Releasing...' : 'Release for Shipment'}
                      </Button>
                    )}
                  </>
                )}

                {/* Ready for Shipment: Shipped */}
                {isAdminOrSupervisor && isOrderReadyToShip && (
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleShipOrder}
                    disabled={isShipping}
                    sx={{ py: 1.5 }}
                  >
                    {isShipping ? 'Shipping...' : 'Shipped'}
                  </Button>
                )}

                {/* On Hold: Release from Hold */}
                {isOrderOnHold && isAdminOrSupervisor && (
                  <Button
                    variant="contained"
                    color="warning"
                    fullWidth
                    onClick={handleReleaseFromHold}
                    disabled={isReleasingFromHold}
                    sx={{ py: 1.5 }}
                  >
                    {isReleasingFromHold ? 'Releasing...' : 'Release from Hold'}
                  </Button>
                )}

                <Divider sx={{ my: 0.5 }} />

                {/* Always-visible navigation buttons */}
                <Tooltip title="Redirect the user to the screen where only the Building and Truss data is managed">
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate(`/order-details/${order.id}/manage-buildings`)}
                    sx={{ py: 1.5 }}
                  >
                    Manage Building Projects
                  </Button>
                </Tooltip>
                {!isDealer && (
                  <Tooltip title="View the Work Order Traveler for production tracking on the shop floor">
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={() => navigate(`/order-details/${order.id}/traveler`)}
                      sx={{ py: 1.5 }}
                    >
                      Work Order Traveler
                    </Button>
                  </Tooltip>
                )}
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Finalize Order Section */}
        <Grid item xs={12}>
          <Section title="Finalize Order">
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                NOTICE AND WARNING
              </Typography>
              <Box
                sx={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 3,
                  backgroundColor: 'grey.50',
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {ORDER_NOTICE_AND_WARNING_TEXT}
                </Typography>
              </Box>
              <Box sx={{ mt: 3 }}>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  Signature: By signing below, you agree to the above notice and warning.
                </Typography>
                {order.signedBy && order.signedDate ? (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Signed by <strong>{order.signedBy}</strong> on{' '}
                    {DateUtils.toDisplayDateTime(order.signedDate)}
                  </Alert>
                ) : null}
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      label="Type Name"
                      placeholder="Enter your name"
                      value={signatureName}
                      onChange={e => setSignatureName(e.target.value)}
                      disabled={!isOrderDraft || !canManageOrders || !!order.signedBy}
                      sx={{ mb: 2 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Type Date"
                      value={
                        order.signedDate
                          ? DateUtils.toDisplayDateTime(order.signedDate)
                          : signatureDate || DateUtils.toDisplayDateTime(new Date().toISOString())
                      }
                      disabled
                      sx={{ mb: 2 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    {isOrderDraft && canManageOrders && !order.signedBy && (
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={handleSignOrder}
                        disabled={isSigning || !signatureName.trim()}
                        sx={{ mb: 2, height: 56 }}
                      >
                        {isSigning ? 'Signing...' : 'Sign Order'}
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Section>
        </Grid>
      </Grid>

      {/* Order Details Dialog */}
      <OrderDetailsDialog
        open={orderDetailsDialogOpen}
        onClose={handleOrderDetailsClose}
        order={order}
        onSubmit={handleOrderDetailsSubmit}
        isSubmitting={isUpdating}
      />

      {/* Order Shipping Dialog */}
      <OrderShippingDialog
        open={orderShippingDialogOpen}
        onClose={handleShippingClose}
        order={order}
        onSubmit={handleShippingSubmit}
        isSubmitting={isUpdating}
      />

      {/* Order Building Info Dialog */}
      <OrderLoadRatingDialog
        open={orderBuildingInfoDialogOpen}
        onClose={handleBuildingInfoClose}
        order={order}
        dealer={dealer}
        onSubmit={handleBuildingInfoSubmit}
        isSubmitting={isUpdating}
      />

      {/* Checkout Confirmation Dialog */}
      <CheckoutConfirmationDialog
        open={checkoutDialogOpen}
        onConfirm={handleConfirmCheckout}
        onCancel={() => setCheckoutDialogOpen(false)}
        isSubmitting={isCheckingOut}
        validationData={checkoutValidation}
        orderNumber={order.orderNumber}
      />

      {/* Job Location Dialog */}
      <JobLocationDialog
        open={jobLocationDialogOpen}
        onClose={handleJobLocationClose}
        order={order}
        onSubmit={handleJobLocationSubmit}
        isSubmitting={isUpdating || isLoadingAsce}
      />

      {/* Confirm Load Rating Update Dialog */}
      {pendingAsceData && (
        <ConfirmLoadRatingDialog
          open={confirmLoadRatingOpen}
          onClose={() => {
            setConfirmLoadRatingOpen(false);
            setPendingAsceData(null);
          }}
          onConfirm={handleConfirmLoadRatingUpdate}
          previousWindLoad={pendingAsceData.previousWindLoad}
          newWindLoad={pendingAsceData.windLoad}
          previousSnowLoad={pendingAsceData.previousSnowLoad}
          newSnowLoad={pendingAsceData.snowLoad}
          isSubmitting={isUpdating}
        />
      )}
    </Box>
  );
};

export default OrderDetailsHeader;
